import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { t } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminAddWordDialog } from "./AdminAddWordDialog";
import { AdminWordRow } from "./AdminWordRow";

export default function AdminPanelPage() {
  const { lang } = useApp();
  const nav = useNavigate();
  const [tab, setTab] = useState("pending");
  const [source, setSource] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    let active = true;
    api
      .get("/admin/me")
      .then(() => active && setAuthed(true))
      .catch(() => active && setAuthed(false));
    return () => {
      active = false;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const srcParam = source === "all" ? "" : `&source_lang=${source}`;
      const r = await api.get(`/admin/words?status=${tab}${srcParam}`);
      setItems(r.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        setAuthed(false);
      }
    } finally {
      setLoading(false);
    }
  }, [tab, source]);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  if (authed === false) {
    return <Navigate to="/admin/login" replace />;
  }

  async function logout() {
    try {
      await api.post("/admin/logout");
    } catch (e) {
      /* ignore */
    }
    nav("/admin/login");
  }

  return (
    <main data-testid="admin-panel" className="max-w-6xl mx-auto px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif-display text-5xl font-bold tracking-tighter">
          {t("admin_queue", lang)}
        </h1>
        <div className="flex items-center gap-3">
          <AdminAddWordDialog onCreated={() => { setTab("approved"); load(); }} lang={lang} />
          <a href="/admin/2fa" data-testid="link-2fa" className="text-xs tracking-eyebrow text-gray-500 hover:text-gray-900">2FA</a>
          <Button
            data-testid="admin-logout"
            variant="outline"
            onClick={logout}
            className="rounded-full"
          >
            {t("logout", lang)}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#F3F1ED]">
            <TabsTrigger data-testid="tab-pending" value="pending">
              {t("pending", lang)}
            </TabsTrigger>
            <TabsTrigger data-testid="tab-approved" value="approved">
              {t("approved", lang)}
            </TabsTrigger>
            <TabsTrigger data-testid="tab-rejected" value="rejected">
              {t("rejected", lang)}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div data-testid="admin-source-filter" className="flex items-center rounded-full border border-gray-300 p-0.5 bg-white">
          {[
            { code: "all", label: "Alle" },
            { code: "no", label: "🇳🇴 Norsk" },
            { code: "es", label: "🇪🇸 Español" },
          ].map((o) => (
            <button
              key={o.code}
              data-testid={`admin-source-${o.code}`}
              onClick={() => setSource(o.code)}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                source === o.code ? "bg-gray-900 text-white" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-gray-500">{t("loading", lang)}</p>
      ) : items.length === 0 ? (
        <p className="text-gray-500 py-12 text-center">— · —</p>
      ) : (
        <div className="space-y-5">
          {items.map((w) => (
            <AdminWordRow key={w.id} word={w} onChanged={load} lang={lang} />
          ))}
        </div>
      )}
    </main>
  );
}
